import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { footerBus } from '@/lib/footer-bus'
import { isMotionPaused, motionStore } from '@/lib/motion'

const WORD = 'NORRVIK'
const SCAN_MS = 1400
const SCAN_BAND = 40
const FLASH_MS = 320
const PUSH_RADIUS = 84
const PUSH = 0.55
const SPRING = 0.02
const DAMPING = 0.72
const RIPPLE_SPEED = 0.75
const RIPPLE_BAND = 20
const LIFT_MS = 1100
const FADE_IN_MS = 500
const BUCKET_ALPHA = [0.42, 0.58, 0.74, 0.9]

interface Field {
    count: number
    size: number
    hx: Float32Array
    hy: Float32Array
    x: Float32Array
    y: Float32Array
    vx: Float32Array
    vy: Float32Array
    seedA: Float32Array
    seedB: Float32Array
    revealAt: Float64Array
    blue: Uint8Array
    buckets: Array<Uint32Array>
}

interface Ripple {
    x: number
    y: number
    start: number
}

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

/**
 * Samples the wordmark, fitted to the box, into a jittered grid of points.
 */
const sample = (width: number, height: number): Field | null => {
    const off = document.createElement('canvas')

    off.width = Math.max(1, Math.round(width))
    off.height = Math.max(1, Math.round(height))

    const context = off.getContext('2d', { willReadFrequently: true })

    if (!context) return null

    const family = getComputedStyle(document.documentElement).getPropertyValue('--font-app-heading').trim() || 'sans-serif'
    const letters = WORD.split('')

    context.font = `600 100px ${family}`

    const widths = letters.map(letter => context.measureText(letter).width)
    const cap = context.measureText('N').actualBoundingBoxAscent || 72
    const spacing = 2.5
    const total = widths.reduce((sum, value) => sum + value, 0) + spacing * (letters.length - 1)
    const scale = Math.min((off.width * 0.985) / total, (off.height * 0.82) / cap)

    context.font = `600 ${100 * scale}px ${family}`
    context.fillStyle = '#fff'
    context.textBaseline = 'alphabetic'

    let cursor = (off.width - total * scale) / 2
    const baseline = off.height / 2 + (cap * scale) / 2

    letters.forEach((letter, index) => {
        context.fillText(letter, cursor, baseline)
        cursor += (widths[index] + spacing) * scale
    })

    const data = context.getImageData(0, 0, off.width, off.height).data
    const step = Math.max(3, Math.round(width / 300))
    const hx: Array<number> = []
    const hy: Array<number> = []

    let seed = 7

    const random = (): number => {
        seed = (seed * 16807) % 2147483647

        return (seed - 1) / 2147483646
    }

    for (let py = Math.floor(step / 2); py < off.height; py += step) {
        for (let px = Math.floor(step / 2); px < off.width; px += step) {
            if (data[(py * off.width + px) * 4 + 3] < 128) continue

            hx.push(px + (random() - 0.5) * step * 0.7)
            hy.push(py + (random() - 0.5) * step * 0.7)
        }
    }

    const count = hx.length
    const seedA = new Float32Array(count)
    const seedB = new Float32Array(count)
    const blue = new Uint8Array(count)
    const lists: Array<Array<number>> = BUCKET_ALPHA.map(() => [])

    for (let i = 0; i < count; i++) {
        seedA[i] = random()
        seedB[i] = random()
        blue[i] = random() < 0.04 ? 1 : 0
        lists[Math.min(BUCKET_ALPHA.length - 1, Math.floor(random() * BUCKET_ALPHA.length))].push(i)
    }

    return {
        count,
        size: Math.max(1.2, step * 0.42),
        hx: Float32Array.from(hx),
        hy: Float32Array.from(hy),
        x: Float32Array.from(hx),
        y: Float32Array.from(hy),
        vx: new Float32Array(count),
        vy: new Float32Array(count),
        seedA,
        seedB,
        revealAt: new Float64Array(count).fill(Number.POSITIVE_INFINITY),
        blue,
        buckets: lists.map(list => Uint32Array.from(list))
    }
}

/**
 * The giant NORRVIK in points. It assembles once behind a blue scan plane when it first comes into
 * view, then the points shy away from the cursor and drift back like disturbed dust. A tap sends a
 * ripple through the letters, and Back to top lifts them away.
 */
export const FooterWordmark: FC = (): ReactNode => {
    const boxRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const box = boxRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!box || !canvas || !context) return

        const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        const still = (): boolean => reducedQuery.matches || isMotionPaused()

        let width = 0
        let height = 0
        let field: Field | null = null
        let state: 'waiting' | 'scanning' | 'ready' = 'waiting'
        let scanStart = 0
        let scanX = -SCAN_BAND
        let raf = 0
        let last = 0
        let visible = false
        let inView = false
        let pointer: { x: number, y: number } | null = null
        let ripples: Array<Ripple> = []
        let liftStart = -1
        let fadeInStart = -1
        let disposed = false

        const snapHome = (): void => {
            if (!field) return

            field.x.set(field.hx)
            field.y.set(field.hy)
            field.vx.fill(0)
            field.vy.fill(0)
            field.revealAt.fill(Number.NEGATIVE_INFINITY)
        }

        const draw = (now: number): void => {
            context.clearRect(0, 0, width, height)

            if (!field || state === 'waiting') return

            let fade = 1

            if (liftStart >= 0) fade = Math.max(0, 1 - (now - liftStart) / LIFT_MS)
            else if (fadeInStart >= 0) fade = Math.min(1, (now - fadeInStart) / FADE_IN_MS)

            const size = field.size

            field.buckets.forEach((list, bucket) => {
                context.fillStyle = `rgba(216, 210, 196, ${(BUCKET_ALPHA[bucket] * fade).toFixed(3)})`

                for (let n = 0; n < list.length; n++) {
                    const i = list[n]

                    if (now < field!.revealAt[i]) continue

                    context.fillRect(field!.x[i], field!.y[i], size, size)
                }
            })

            if (state === 'scanning') {
                for (let i = 0; i < field.count; i++) {
                    const age = now - field.revealAt[i]

                    if (age < 0 || age > FLASH_MS) continue

                    const strength = 1 - age / FLASH_MS

                    context.fillStyle = field.blue[i] ? `rgba(134, 182, 240, ${strength.toFixed(3)})` : `rgba(255, 249, 239, ${strength.toFixed(3)})`
                    context.fillRect(field.x[i] - 0.4, field.y[i] - 0.4, size + 0.8, size + 0.8)
                }

                context.fillStyle = 'rgba(134, 182, 240, 0.06)'
                context.fillRect(scanX - SCAN_BAND, 0, SCAN_BAND, height)
                context.fillStyle = 'rgba(134, 182, 240, 0.95)'
                context.fillRect(scanX, 0, 1, height)
                context.fillRect(scanX - 4, 0, 9, 1)
                context.fillRect(scanX - 4, height - 1, 9, 1)
            }
        }

        const advance = (now: number, k: number): boolean => {
            if (!field) return false

            if (state === 'scanning') {
                const t = Math.min(1, (now - scanStart) / SCAN_MS)

                scanX = -SCAN_BAND + (width + SCAN_BAND * 2) * easeInOut(t)

                for (let i = 0; i < field.count; i++) {
                    if (field.revealAt[i] !== Number.POSITIVE_INFINITY || field.hx[i] > scanX) continue

                    field.revealAt[i] = now
                    field.x[i] = field.hx[i] + (field.seedA[i] - 0.5) * 22
                    field.y[i] = field.hy[i] + (field.seedB[i] - 0.5) * 22
                }

                if (t >= 1 && now - scanStart > SCAN_MS + FLASH_MS) state = 'ready'
            }

            const damping = Math.pow(DAMPING, k)
            const lifting = liftStart >= 0

            let moving = false

            for (let i = 0; i < field.count; i++) {
                if (now < field.revealAt[i]) continue

                let x = field.x[i]
                let y = field.y[i]
                let vx = field.vx[i]
                let vy = field.vy[i]

                if (lifting) {
                    vy -= 0.14 * k
                    x += vx * k
                    y += vy * k
                    field.x[i] = x
                    field.y[i] = y
                    field.vy[i] = vy

                    continue
                }

                let fx = (field.hx[i] - x) * SPRING
                let fy = (field.hy[i] - y) * SPRING

                if (pointer) {
                    const dx = x - pointer.x
                    const dy = y - pointer.y
                    const d2 = dx * dx + dy * dy

                    if (d2 < PUSH_RADIUS * PUSH_RADIUS) {
                        const d = Math.sqrt(d2) || 0.001
                        const f = (1 - d / PUSH_RADIUS) * (1 - d / PUSH_RADIUS) * PUSH

                        fx += (dx / d) * f
                        fy += (dy / d) * f
                    }
                }

                for (const ripple of ripples) {
                    const dx = x - ripple.x
                    const dy = y - ripple.y
                    const d = Math.sqrt(dx * dx + dy * dy) || 0.001
                    const offset = Math.abs(d - (now - ripple.start) * RIPPLE_SPEED)

                    if (offset < RIPPLE_BAND) {
                        const f = (1 - offset / RIPPLE_BAND) * 0.9

                        fx += (dx / d) * f
                        fy += (dy / d) * f
                    }
                }

                vx = (vx + fx * k) * damping
                vy = (vy + fy * k) * damping
                x += vx * k
                y += vy * k

                field.x[i] = x
                field.y[i] = y
                field.vx[i] = vx
                field.vy[i] = vy

                if (Math.abs(vx) + Math.abs(vy) > 0.01 || Math.abs(x - field.hx[i]) + Math.abs(y - field.hy[i]) > 0.05) moving = true
            }

            const reach = Math.hypot(width, height) + RIPPLE_BAND

            ripples = ripples.filter(ripple => (now - ripple.start) * RIPPLE_SPEED < reach)

            if (lifting && now - liftStart > LIFT_MS + 200) {
                liftStart = -1
                snapHome()
                fadeInStart = now
            }

            if (fadeInStart >= 0 && now - fadeInStart > FADE_IN_MS) fadeInStart = -1

            return moving || state === 'scanning' || ripples.length > 0 || liftStart >= 0 || fadeInStart >= 0
        }

        const frame = (now: number): void => {
            raf = 0

            const k = last ? Math.min(3, (now - last) / 16.667) : 1

            last = now

            const active = advance(now, k)

            draw(now)

            if (visible && active && !still()) raf = requestAnimationFrame(frame)
            else last = 0
        }

        const kick = (): void => {
            if (raf || !visible || still() || disposed) return

            last = 0
            raf = requestAnimationFrame(frame)
        }

        const settle = (): void => {
            cancelAnimationFrame(raf)
            raf = 0
            ripples = []
            liftStart = -1
            fadeInStart = -1

            if (state !== 'waiting') state = 'ready'

            snapHome()
            draw(performance.now())
        }

        const start = (): void => {
            if (state !== 'waiting' || !field) return

            if (still()) {
                state = 'ready'
                snapHome()
                draw(performance.now())

                return
            }

            state = 'scanning'
            scanStart = performance.now()
            kick()
        }

        const build = (): void => {
            const rect = box.getBoundingClientRect()
            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            if (rect.width < 2 || rect.height < 2) return

            width = rect.width
            height = rect.height
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
            field = sample(width, height)

            if (state !== 'waiting') {
                state = 'ready'
                snapHome()
            }

            draw(performance.now())

            if (inView) start()
        }

        let resizeRaf = 0

        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(resizeRaf)
            resizeRaf = requestAnimationFrame(build)
        })

        const intersection = new IntersectionObserver(entries => {
            const entry = entries[entries.length - 1]

            visible = entry.isIntersecting
            inView = visible && entry.intersectionRatio >= 0.35

            if (inView) start()

            if (visible) kick()
        }, { threshold: [0, 0.35] })

        const ready = document.fonts?.ready ?? Promise.resolve()

        ready.then(() => {
            if (disposed) return

            build()
            resizeObserver.observe(box)
            intersection.observe(box)
        }).catch(() => {
            build()
        })

        const local = (event: PointerEvent): { x: number, y: number } => {
            const rect = canvas.getBoundingClientRect()

            return { x: event.clientX - rect.left, y: event.clientY - rect.top }
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse' || still()) return

            pointer = local(event)
            kick()
        }

        const onLeave = (): void => {
            pointer = null
            kick()
        }

        const onDown = (event: PointerEvent): void => {
            if (event.pointerType === 'mouse' || still() || state === 'waiting') return

            const point = local(event)

            ripples.push({ x: point.x, y: point.y, start: performance.now() })
            kick()
        }

        canvas.addEventListener('pointermove', onMove)
        canvas.addEventListener('pointerleave', onLeave)
        canvas.addEventListener('pointerdown', onDown)

        const offLift = footerBus.on('lift', () => {
            if (still() || state === 'waiting' || !field) return

            if (state === 'scanning') {
                state = 'ready'
                field.revealAt.fill(Number.NEGATIVE_INFINITY)
            }

            liftStart = performance.now()
            fadeInStart = -1

            for (let i = 0; i < field.count; i++) {
                field.vx[i] = (field.seedA[i] - 0.5) * 0.8
                field.vy[i] = -(2 + field.seedB[i] * 5)
            }

            kick()
        })

        const offMotion = motionStore.subscribe(() => {
            if (isMotionPaused()) settle()
        })

        const onReducedChange = (): void => {
            if (reducedQuery.matches) settle()
        }

        reducedQuery.addEventListener('change', onReducedChange)

        return () => {
            disposed = true
            cancelAnimationFrame(raf)
            cancelAnimationFrame(resizeRaf)
            resizeObserver.disconnect()
            intersection.disconnect()
            canvas.removeEventListener('pointermove', onMove)
            canvas.removeEventListener('pointerleave', onLeave)
            canvas.removeEventListener('pointerdown', onDown)
            reducedQuery.removeEventListener('change', onReducedChange)
            offLift()
            offMotion()
        }
    }, [])

    return (
        <div ref={boxRef} role='img' aria-label='Norrvik' className='relative aspect-[100/21] w-full'>
            <canvas ref={canvasRef} className='absolute inset-0 block h-full w-full touch-pan-y' aria-hidden='true' />
        </div>
    )
}
